import { DrawerContentProps, UseDisclosureReturn } from "@chakra-ui/react"
import React from "react"
import { exhaustiveGuard } from "../../utils/typing"
import {
  AugmentationCoordinatorProvider,
  AugmentationLayoutFrame,
  AugmentationPrimarySurface,
  useAugmentationCoordinator,
  useAugmentationLayoutSpacing,
  useAugmentationMeasure,
  useAugmentationPanelOffset,
  useAugmentationToolbarOffset
} from "./AugmentationCoordinator"
import AugmentationPositionedToolbar from "./AugmentationPositionedToolbar"
import AugmentationToolbar, {
  AugmentationToolbarHookReturn,
  isOptionVisible,
  useAugmentationDisclosedToolbar
} from "./AugmentationToolbar"
import { AugmentationOffsetOptions } from "./useAugmentationOffset"
import { UseAugmentationPanelProps } from "./useAugmentationPanel"

export type AugmentationPanel = ReturnType<
  typeof React.forwardRef<
    HTMLElement,
    UseAugmentationPanelProps & {
      toolbar: AugmentationToolbarHookReturn
      disclosure: UseDisclosureReturn
      drawerContentProps?: Partial<DrawerContentProps>
      /** Render the Drawer positioner in Chakra's top-level portal. */
      usePortal?: boolean
      /** Trap focus within the Drawer while it is open. */
      trapFocus?: boolean
      /** Isolate a host's panel route from embedded panels (defaults to "panel"). */
      queryParamKey?: string
    }
  >
>

// The AugmentationComponents is an object that is keyed with  "Primary", "Panel",
// and optionally "Toolbar" to allow for specifying the order of the components
// (since an object's keys will retain order when doing Object.keys()).
// This can (potentially) be used to determine the order of the components
// (like whether the panel is to the left or right of the primary view).
export type AugmentationComponents = {
  Primary?: React.FC<any>
  Panel: AugmentationPanel
  Toolbar?: typeof AugmentationToolbar | typeof AugmentationPositionedToolbar
}

export type AugmentationLayoutProps = {
  components: AugmentationComponents
  /** When provided (and no components.Primary), rendered as the primary surface. */
  children?: React.ReactNode
  toolbar: AugmentationToolbarHookReturn
  disclosure: UseDisclosureReturn
  // Spacing between layout elements.
  spacing?: number | [number, number] | [number, number, number]
  // Allow the augmentation panel to completely overlay the primary view at smaller screen sizes.
  allowOverlay?: boolean
  // Whether a full-screen open panel should hide its bottom toolbar.
  // Hide, don't unmount — otherwise the enter stagger replays on close.
  toolbarYieldsToPanel?: boolean
  // Position the Panel relative to the Primary view.
  panelPosition?: "near" | "far"
  // Offsetting is enabled by default. Pass false to disable or an object to customize.
  offset?: AugmentationOffsetOptions
  panelSize?: UseAugmentationPanelProps["size"]
  drawerContentProps?: Partial<DrawerContentProps>
  /** Explicit alignment. Defaults to right. Left panel support is incomplete. */
  align?: "left" | "right"
}

function AugmentationLayoutContent({
  components,
  children,
  toolbar,
  disclosure,
  allowOverlay,
  toolbarYieldsToPanel,
  panelSize,
  drawerContentProps,
  hasToolbar
}: AugmentationLayoutProps & { hasToolbar: boolean }) {
  const { Primary, Panel, Toolbar } = components
  const disclosedToolbar = useAugmentationDisclosedToolbar(toolbar, disclosure)
  const layoutSpacing = useAugmentationLayoutSpacing()
  const panelRef = useAugmentationMeasure("panel")
  const toolbarRef = useAugmentationMeasure("toolbar")
  const panelOffset = useAugmentationPanelOffset()
  const toolbarOffset = useAugmentationToolbarOffset()
  const { panelTokenSize } = useAugmentationCoordinator()
  const toolbarYields =
    !!toolbarYieldsToPanel && disclosure.open && panelTokenSize === "full"

  const primaryContent = Primary ? (
    // TODO: remove this dependency OR type the Primary props as such.
    <Primary {...disclosedToolbar} />
  ) : (
    children
  )

  const renderKey = (key: keyof AugmentationComponents) => {
    switch (key) {
      case "Primary":
        return primaryContent ? (
          <AugmentationPrimarySurface key={key}>
            {primaryContent}
          </AugmentationPrimarySurface>
        ) : null
      case "Panel":
        return (
          <Panel
            key={key}
            ref={panelRef}
            toolbar={disclosedToolbar}
            disclosure={disclosure}
            allowOverlay={allowOverlay}
            size={panelSize}
            gutter={layoutSpacing}
            drawerContentProps={drawerContentProps}
            offset={{
              ...panelOffset,
              ...(toolbarYields ? { bottom: 0 } : {})
            }}
          />
        )
      case "Toolbar":
        // Skip mounting when there are no visible options so offset readiness
        // is not blocked waiting on a toolbar width that will never exist.
        return Toolbar && hasToolbar ? (
          <Toolbar
            key={key}
            ref={toolbarRef}
            allowOverlay={allowOverlay}
            gutter={layoutSpacing}
            offset={toolbarOffset}
            toolbar={disclosedToolbar}
            yieldsToPanel={toolbarYields}
          />
        ) : null
      default:
        exhaustiveGuard(key)
    }
  }

  // Preserve declared component key order for render order.
  const keys = Object.keys(components) as (keyof AugmentationComponents)[]
  // If Primary is omitted from components but children are provided, render
  // the primary surface before Panel/Toolbar.
  const hasPrimaryKey = keys.includes("Primary")
  const content = (
    <>
      {!hasPrimaryKey && primaryContent ? (
        <AugmentationPrimarySurface>
          {primaryContent}
        </AugmentationPrimarySurface>
      ) : null}
      {keys.map(renderKey)}
    </>
  )

  return <AugmentationLayoutFrame>{content}</AugmentationLayoutFrame>
}

/**
 * Convenience composer for Primary / Panel / Toolbar.
 * Coordination (measurement + offsets) lives in AugmentationCoordinatorProvider;
 * pieces may also be composed independently under that provider.
 */
const AugmentationLayout = (props: AugmentationLayoutProps) => {
  const { components, children, toolbar } = props
  const hasPrimary = Boolean(components.Primary || children)
  // Invisible options can still drive a routed panel, but AugmentationToolbar
  // itself does not render. Treat that as no toolbar so Primary offset is not
  // blocked waiting on a toolbar width that will never exist.
  const hasToolbar =
    Boolean(components.Toolbar) && toolbar.options.some(isOptionVisible)

  // Choose to align the augmentation (panel + toolbar) to the left or right.
  // This will be used to determine the offset of the primary view.
  // Fall back to key-order alignment when align is not explicit.
  // NOTE: aligning panel to the left does not currently work. This requires
  // updating the useAugmentationPanel hook to support left alignment.
  // Also, toolbar doesn't respect the alignment either.
  const alignFromKeys =
    Object.keys(components).indexOf("Panel") <
    Object.keys(components).indexOf("Primary")
      ? "left"
      : "right"
  const align = props.align ?? alignFromKeys

  return (
    <AugmentationCoordinatorProvider
      disclosure={props.disclosure}
      spacing={props.spacing}
      allowOverlay={props.allowOverlay}
      panelPosition={props.panelPosition}
      offset={props.offset}
      panelSize={props.panelSize}
      align={align}
      hasPrimary={hasPrimary}
      hasToolbar={hasToolbar}
    >
      <AugmentationLayoutContent {...props} hasToolbar={hasToolbar} />
    </AugmentationCoordinatorProvider>
  )
}

export default AugmentationLayout
